import { useState } from 'react';
import { ModalWrapper } from '../../ui';
import type { Ticket, TicketStatus, TicketPriority } from '../types';
import { mockTickets } from '../services/mockData';

interface TicketDetailsProps {
  isOpen: boolean;
  ticketId?: string | null;
  onClose: () => void;
  onEdit: (ticketId: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-sky-100 text-sky-700',
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  reopened: 'bg-red-100 text-red-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
};

export default function TicketDetails({ isOpen, ticketId, onClose, onEdit }: TicketDetailsProps) {
  const ticket = ticketId ? mockTickets.find(t => t.id === ticketId) : undefined;
  const [newComment, setNewComment] = useState('');

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleAddComment = () => {
    if (newComment.trim()) {
      console.log('Adding comment:', newComment);
      setNewComment('');
    }
  };

  if (!ticket) return null;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={ticket.title}
      subtitle={`${ticket.id} — ${ticket.category}`}
      icon="ri-ticket-2-line"
      gradient="from-blue-600 via-indigo-600 to-purple-600"
      size="xl"
      footerActions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => onEdit(ticket.id)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <i className="ri-edit-line"></i> Edit
          </button>
        </div>
      }
    >
      <div className="p-6">
        {/* Status & Priority badges */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-700'}`}>
            {ticket.status.replace('_', ' ').toUpperCase()}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[ticket.priority] || 'bg-gray-100 text-gray-700'}`}>
            {ticket.priority.toUpperCase()}
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            {ticket.severity.charAt(0).toUpperCase() + ticket.severity.slice(1)}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content — 2/3 */}
          <div className="lg:col-span-2 space-y-5">
            {/* Description */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
            </div>

            {/* Tags */}
            {ticket.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {ticket.tags.map((tag, i) => (
                  <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{tag}</span>
                ))}
              </div>
            )}

            {/* Comments */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Comments ({ticket.comments.length})
              </h3>
              {ticket.comments.length === 0 ? (
                <p className="text-sm text-gray-400">No comments yet</p>
              ) : (
                <div className="space-y-3 mb-4">
                  {ticket.comments.map(comment => (
                    <div key={comment.id} className="border-l-2 border-blue-200 pl-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">{comment.author}</span>
                        <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <textarea
                  rows={3}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                  >
                    <i className="ri-send-plane-line"></i> Add Comment
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar — 1/3 */}
          <div className="space-y-4">
            {/* Ticket Info */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Ticket Information</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Category</span>
                  <p className="text-gray-900">{ticket.category}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Component</span>
                  <p className="text-gray-900">{ticket.component}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Version</span>
                  <p className="text-gray-900">{ticket.version}</p>
                </div>
                <hr className="border-gray-100" />
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Reporter</span>
                  <p className="text-gray-900">{ticket.reporter}</p>
                  <p className="text-xs text-gray-400">{ticket.reporter_email}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Assignee</span>
                  <p className="text-gray-900">{ticket.assignee || 'Unassigned'}</p>
                  {ticket.assignee_email && <p className="text-xs text-gray-400">{ticket.assignee_email}</p>}
                </div>
                <hr className="border-gray-100" />
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Created</span>
                  <p className="text-gray-900">{formatDate(ticket.created_at)}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">Updated</span>
                  <p className="text-gray-900">{formatDate(ticket.updated_at)}</p>
                </div>
                {ticket.resolved_at && (
                  <div>
                    <span className="text-xs text-gray-400 uppercase tracking-wide">Resolved</span>
                    <p className="text-gray-900">{formatDate(ticket.resolved_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Attachments ({ticket.attachments.length})
              </h3>
              {ticket.attachments.length === 0 ? (
                <p className="text-sm text-gray-400">No attachments</p>
              ) : (
                <div className="space-y-2">
                  {ticket.attachments.map(a => (
                    <div key={a.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700 truncate">{a.filename}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{(a.file_size / 1024).toFixed(1)} KB</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
